/**
 * Versioned AI feedback config for the teacher-feedback app.
 *
 * Stored under:
 * - `teacherFeedback/data/courses/{courseId}/feedbackConfig/current` — pointer doc
 * - `teacherFeedback/data/courses/{courseId}/feedbackConfig/{configId}` — immutable versions
 *
 * Note: version docs and the `current` pointer share the same Firestore
 * collection. `current` is a reserved document id; version ids are random and
 * will not collide. If callers ever list this collection they must skip the
 * `current` id (its schema differs from a version row).
 */
import {z} from 'zod';

import {FirestoreTimestampSchema} from '../../types';

// ============================================================================
// Output field descriptors
// ============================================================================

const FEEDBACK_OUTPUT_FIELD_TYPES = ['text', 'moment'] as const;

/** Allowed values for `FeedbackOutputField.fieldType`. */
export const FeedbackOutputFieldTypeSchema = z.enum(
  FEEDBACK_OUTPUT_FIELD_TYPES,
);

/** One machine-readable output slot for the LLM and UI. */
export const FeedbackOutputFieldSchema = z.object({
  /** Machine-readable key (matches `feedback` record keys on session feedback docs). */
  key: z.string(),
  /** Human-readable label for display. */
  label: z.string(),
  /** Description passed through to the LLM / JSON Schema. */
  description: z.string(),
  /** `text` = freeform string; `moment` = transcript timestamp range. */
  fieldType: FeedbackOutputFieldTypeSchema,
  /** When true, the field is listed in JSON Schema `required`. */
  required: z.boolean().optional(),
});

export type FeedbackOutputField = z.infer<typeof FeedbackOutputFieldSchema>;
export type FeedbackOutputFieldType = z.infer<
  typeof FeedbackOutputFieldTypeSchema
>;

// ============================================================================
// Config version document
// ============================================================================

/**
 * Immutable versioned config stored at
 * `feedbackConfig/{configId}`.
 *
 * Written when an admin saves settings; never mutated in place. The
 * `configId` matches the document ID in the `feedbackConfig` collection and is
 * referenced by pointer docs and by session feedback docs
 * (`sessionFeedback.ts`) via `configId` plus a frozen `configSnapshot`.
 */
export const TeacherFeedbackConfigSchema = z.object({
  /** Same as the Firestore document id under `feedbackConfig/`. */
  configId: z.string(),
  /** System message for the LLM (plain string; no template engine). */
  systemPrompt: z.string(),
  /** Optional human-facing summary of what this config is for. */
  description: z.string().optional(),
  /** Declares output keys, types, and JSON Schema metadata for generation + UI. */
  outputFields: z.array(FeedbackOutputFieldSchema),
  /** Chat Completions model id; callers default when omitted (e.g. `gpt-4.1`). */
  model: z.string().optional(),
  /** Sampling temperature passed to the API when set. */
  temperature: z.number().optional(),
  /**
   * Override names used to identify the teacher in the transcript when the
   * webhook lookup of the Zoom display name fails. Matching is
   * case-insensitive. Defaults to `['Teacher']`; falls back further to the
   * most-frequent speaker when no name matches.
   */
  primarySpeakerNames: z.array(z.string()).optional(),
  /** When this version row was created (server time). */
  createdAt: FirestoreTimestampSchema,
});

export type TeacherFeedbackConfig = z.infer<typeof TeacherFeedbackConfigSchema>;

// ============================================================================
// Current pointer document
// ============================================================================

/**
 * Pointer doc at `feedbackConfig/current` — identifies the active version ID.
 */
export const TeacherFeedbackConfigCurrentSchema = z.object({
  activeConfigId: z.string(),
});

export type TeacherFeedbackConfigCurrent = z.infer<
  typeof TeacherFeedbackConfigCurrentSchema
>;

// ============================================================================
// Paths
// ============================================================================

const feedbackConfigRoot = ({courseId}: {courseId: string}): string =>
  `teacherFeedback/data/courses/${courseId}/feedbackConfig`;

/**
 * Path to the active-config pointer document.
 * `teacherFeedback/data/courses/{courseId}/feedbackConfig/current`
 */
export function getTeacherFeedbackFeedbackConfigCurrentDocumentPath({
  courseId,
}: {
  courseId: string;
}): string {
  return `${feedbackConfigRoot({courseId})}/current`;
}

/**
 * Collection path for versioned configs. Shares a collection with the
 * `current` pointer doc; the pointer uses the reserved id `current`.
 * `teacherFeedback/data/courses/{courseId}/feedbackConfig`
 */
export function getTeacherFeedbackFeedbackConfigVersionsCollectionPath({
  courseId,
}: {
  courseId: string;
}): string {
  return feedbackConfigRoot({courseId});
}

/**
 * Path to a single version document.
 * `teacherFeedback/data/courses/{courseId}/feedbackConfig/{configId}`
 */
export function getTeacherFeedbackFeedbackConfigVersionDocumentPath({
  courseId,
  configId,
}: {
  courseId: string;
  configId: string;
}): string {
  return `${getTeacherFeedbackFeedbackConfigVersionsCollectionPath({courseId})}/${configId}`;
}
